import React, { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getter } from "@progress/kendo-react-common";
import { useSelector } from "react-redux";
import { DisplayMsg } from "../../../services/toastService";
import { loadMergeTicketGridData } from "../../../handlers/MergeTicketGridHandler";
import { useKendoGridHandlers } from "../../../CustomHooks/useKendoGridHandlers";
import useReloadButton from "../../../CustomHooks/useReloadButton";
import CustomCell from "../../Grid/CustomCell";
import { setUrl } from "../../../handlers";

const DATA_ITEM_KEY = "TICKET_NO";
const SELECTED_FIELD = "selected";

const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_MERGE = {
  action: "SEARCHTICKETS",
  className: "DASHBOARD",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "CREATED_DATE",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const KendoMergeTicketGrid = React.memo((props) => {
  const filterData = useSelector((store) => store.ticketTaskFilter);
  const sharedData = useSelector((store) => store.SharedLayoutData);

  const { gridInfo } = props;
  const [gridData, setGridData] = useState([]);
  const [total, setTotal] = useState(0);
  const [reloadToggle, setReloadToggle] = useState(false);

  const [gridState, setGridState] = useState({
    pageNumber: DEFAULT_GRID_INFO_MERGE.currentPageNumber,
    pageSize: DEFAULT_GRID_INFO_MERGE.pageSize,
    sortName: DEFAULT_GRID_INFO_MERGE.sortName,
    sortOrder: DEFAULT_GRID_INFO_MERGE.sortOrder,
    sort: [
      {
        field: DEFAULT_GRID_INFO_MERGE.sortName,
        dir: DEFAULT_GRID_INFO_MERGE.sortOrder?.toLowerCase() || "asc",
      },
    ],
  });

  const [selectedState, setSelectedState] = React.useState();

  const { HandleDataStateChange, HandlePageChange } =
    useKendoGridHandlers(setGridState);

  // UseEffect to load the grid data
  useEffect(() => {
    loadMergeTicketGridData(filterData, gridState, sharedData).then(
      (response) => {
        setGridData(response.rows);
        setTotal(response.total);
      }
    );
  }, [gridState, reloadToggle]);

  useReloadButton(setReloadToggle);

  const SelectedContentLength = () => {
    const selectedText = window.getSelection().toString();
    return selectedText.length > 0;
  };

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = SelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });

        setSelectedState(newSelectedState);

        let updateTickets = [event.dataItems];
        const curTicket = updateTickets[0].findIndex(
          (item) =>
            item.TICKET_NO === event.dataItems[event.endRowIndex].TICKET_NO
        );

        updateTickets = updateTickets[0];
        updateTickets[curTicket].selected = !updateTickets[curTicket].selected;
        setGridData(updateTickets);
      }
    },
    [selectedState]
  );

  const CellComments = (props) => (
    <td className="Comments30 pl-4">
      <span className="textWrap" title={props.dataItem[props.field]}>
        {props.dataItem[props.field]}
      </span>
    </td>
  );

  const TriggerPopup = () => {
    props.parentCallback(false);
  };

  const MergeTicket = async () => {
    try {
      let str = "";
      gridData.forEach((item) => {
        if (item.selected === true) {
          str += `${item.TICKET_NO},`;
        }
      });
      if (!str) {
        DisplayMsg("2", "Please select record");
        return;
      }
      // var ticketNo = sharedData.currentTicketNo;
      const userName = sessionStorage.USER;
      const formdata = new FormData();
      formdata.append("USERNAME", userName);
      formdata.append("PARENTTICKET", sharedData.currentTicketNo);
      formdata.append("TICKETS", str);
      document.getElementById("loader").style.display = "block";
      const result = await fetch(setUrl("TICKETDETAIL", "UPDATPARENTTICKET"), {
        method: "POST",
        body: formdata,
      });
      const response = await result.json();
      if (response.status === true) {
        DisplayMsg("1", response.Message);
        TriggerPopup();
      } else {
        DisplayMsg("2", response.Message);
      }
    } catch (e) {
      const param = "";
      const detailExcep = new FormData();
      document.getElementById("loader").style.display = "none";
      detailExcep.append("CLASS_NAME", "MergeTickets.js");
      detailExcep.append("METHOD_NAME", "onClickMergeTicket");
      detailExcep.append("METHOD_PARAM=params", param);
      detailExcep.append("EXCEPTION_MSG", e.message);
      detailExcep.append("STACK_TRACE", e.stack);
      const result = await fetch(setUrl("PORTALLOGS", "CLIENTEXCEPTION"), {
        method: "POST",
        body: detailExcep,
      });
    }
  };

  const CellStatus = (props) => {
    if (
      props.dataItem.STATUS === "O" ||
      props.dataItem.STATUS === "o" ||
      props.dataItem.STATUS === "Open"
    ) {
      props.dataItem.STATUS = "Open";
      return (
        <td>
          <div className="flex">
            <div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "K" ||
      props.dataItem.STATUS === "k" ||
      props.dataItem.STATUS === "Reopened"
    ) {
      props.dataItem.STATUS = "Reopened";
      return (
        <td>
          <div className="flex">
            <div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "I" ||
      props.dataItem.STATUS === "i" ||
      props.dataItem.STATUS === "In Progress"
    ) {
      props.dataItem.STATUS = "In Progress";
      return (
        <td>
          <div className="flex">
            <div className="ticket-inProgrees ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "Q" ||
      props.dataItem.STATUS === "q" ||
      props.dataItem.STATUS === "Pending On Client"
    ) {
      props.dataItem.STATUS = "Pending On Client";
      return (
        <td>
          <div className="flex">
            <div className="ticket-pendingOnClient ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
    if (
      props.dataItem.STATUS === "R" ||
      props.dataItem.STATUS === "r" ||
      props.dataItem.STATUS === "Resolved"
    ) {
      props.dataItem.STATUS = "Resolved";
      return (
        <td>
          <div className="flex">
            <div className="ticket-resolved ticket-status w-100 m-0">
              {props.dataItem.STATUS}
            </div>
          </div>
        </td>
      );
    }
  };

  const GetCustomCell = useCallback(
    (cellProps) => <CustomCell {...cellProps} {...cellProps.additionalProps} />,
    []
  );

  const onHeaderSelectionChange = (event) => {
    const checkboxElement = event.syntheticEvent.target;
    const { checked } = checkboxElement;
    const selectedStateNew = {};

    const TicketCheck = [...gridData];
    for (const [index] of gridData.entries()) {
      TicketCheck[index] = { ...TicketCheck[index], selected: checked };
    }
    setGridData(TicketCheck);

    gridData.forEach((item) => {
      selectedStateNew[idGetter(item)] = checked;
    });

    setSelectedState(selectedStateNew);
  };

  return (
    <div>
      <Tooltip openDelay={100} position="right" anchorElement="target">
        <Grid
          style={{
            height: "calc(100vh - 394px)",
          }}
          scrollable="scrollable"
          total={total}
          skip={(gridState.pageNumber - 1) * gridState.pageSize}
          pageSize={gridState.pageSize}
          filterable={false}
          sortable={{
            mode: "single",
            allowUnsort: false,
          }}
          sort={gridState.sort}
          selectedField={SELECTED_FIELD}
          data={gridData.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
          }))}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          pageable={PAGER_SETTINGS}
          onHeaderSelectionChange={onHeaderSelectionChange}
          onPageChange={HandlePageChange}
          onDataStateChange={HandleDataStateChange}
          onSelectionChange={onSelectionChange}
        >
          <GridColumn
            field={SELECTED_FIELD}
            width="50px"
            className="pl-3"
            headerSelectionValue={
              selectedState &&
              gridData.findIndex((item) => !selectedState[idGetter(item)]) ===
                -1
            }
          />

          <GridColumn title="Status" cell={CellStatus} field="STATUS" />
          <GridColumn title="Team" field="TEAMNAME" filterable={false} />
          <GridColumn title="Ticket #" field="TICKET_NO" sortable />
          <GridColumn
            title="Description"
            field="COMMENTS"
            filterable={false}
            cell={CellComments}
          />
          <GridColumn
            title="Practice Name"
            field="ENTITY_DESCRIPTION"
            cell={CellComments}
            width={150}
          />
          <GridColumn
            title="Assignee"
            field="A_SHORT_NAME"
            cell={CellComments}
          />
          <GridColumn
            title="Created Date"
            field="CREATED_DATE"
            cell={GetCustomCell}
            sortable
            width={150}
          />
        </Grid>
        <div className="col-12" style={{ textAlign: "center" }}>
          <input
            type="button"
            style={{ marginTop: "10px", marginBottom: "10px" }}
            className="btnLinkGreen btnMergeTickets"
            onClick={() => MergeTicket()}
            id="btnMergeTickets"
            value="Merge"
          />
        </div>
      </Tooltip>
    </div>
  );
});
export default KendoMergeTicketGrid;
